import { Button, useMediaQuery } from "@mui/material";
import { Link, useNavigate, useLocation } from "react-router-dom";
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { FLAVOR } from '../../../Config/firebase';
import React, { useEffect, useState, useMemo, useCallback, memo } from 'react';
import { getCurrentFestival, setMockFestival } from '../../../utils/festivalDates';
import DashboardViewSelector, { DashboardView } from './MyDashboard/DashboardViewSelector';
import { motion } from 'framer-motion';
import { useAppSelector } from '../../../state/state';
import type { RootState } from '../../../state/state';
import type { SubscriptionDocWithId } from '../../Subscriptions/types/SubscriptionType';
import { selectActiveRole } from '../../../state/UserStatesV2/userSliceV2';

const ComingSoonRibbon = () => {
    return (
        <div className="absolute top-5 -right-8 rotate-45 bg-gradient-to-r from-blue-600 to-purple-500 text-white py-1 px-10 text-sm font-medium shadow-lg z-20">
            Coming Soon
        </div>
    );
};

const FestivalBanner = () => {
    const [currentFestival, setCurrentFestival] = useState(getCurrentFestival());
    const [showFireworks, setShowFireworks] = useState(true);
    const [key, setKey] = useState(Date.now());

    // Memoize the festival name to prevent unnecessary re-renders
    const festivalName = useMemo(() => currentFestival?.name, [currentFestival]);

    // Update festival when it changes
    useEffect(() => {
        const checkFestival = () => {
            const newFestival = getCurrentFestival();
            if (newFestival?.name !== festivalName) {
                setCurrentFestival(newFestival);
                setShowFireworks(true);
                setKey(Date.now());
            }
        };

        // Check initially
        checkFestival();

        // Set up interval to check every minute instead of continuous checks
        const intervalId = setInterval(checkFestival, 60000);

        // Clean up interval on unmount
        return () => clearInterval(intervalId);
    }, [festivalName]);

    useEffect(() => {
        if (showFireworks) {
            const timer = setTimeout(() => {
                setShowFireworks(false);
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [showFireworks]);

    // Memoize the festival styles to prevent recalculation on every render
    const getFestivalStyles = useCallback(() => {
        if (!currentFestival) {
            return {
                background: 'linear-gradient(45deg, #3B82F6, #6366F1, #8B5CF6)',
                textGradient: 'linear-gradient(to right, #FFFFFF, #E0E0E0)',
                icon: '🎉',
                special: null
            };
        }
        
        switch(currentFestival.name) {
            case 'Diwali':
                return {
                    background: 'linear-gradient(45deg, #FF9933, #FF0066, #9933FF)',
                    textGradient: 'linear-gradient(to right, #FFD700, #FFA500)',
                    icon: '🪔', // Diya lamp
                    special: showFireworks && (
                        <>
                            <div className="firework" style={{ '--x': '20%', '--y': '60%' } as React.CSSProperties}></div>
                            <div className="firework" style={{ '--x': '60%', '--y': '40%' } as React.CSSProperties}></div>
                            <div className="firework" style={{ '--x': '80%', '--y': '70%' } as React.CSSProperties}></div>
                            <div className="diya-flame"></div>
                            <div className="sparkles"></div>
                        </>
                    )
                };
            case 'Holi':
                return {
                    background: 'linear-gradient(45deg, #FF0000, #FF69B4, #4CAF50, #9C27B0)',
                    textGradient: 'linear-gradient(to right, #FFD700, #FF69B4)',
                    icon: '🎨', // Color palette
                    special: showFireworks && (
                        <>
                            <div className="color-splash" style={{ '--x': '10%', '--y': '20%', '--color': '#FF0000' } as React.CSSProperties}></div>
                            <div className="color-splash" style={{ '--x': '30%', '--y': '60%', '--color': '#4CAF50' } as React.CSSProperties}></div>
                            <div className="color-splash" style={{ '--x': '70%', '--y': '40%', '--color': '#FF69B4' } as React.CSSProperties}></div>
                            <div className="color-splash" style={{ '--x': '90%', '--y': '70%', '--color': '#9C27B0' } as React.CSSProperties}></div>
                            <div className="rainbow-overlay"></div>
                        </>
                    )
                };
            case 'Ganesh Chaturthi':
                return {
                    background: 'linear-gradient(45deg, #FF9933, #FF5733, #FFC300)',
                    textGradient: 'linear-gradient(to right, #FFD700, #FF8C00)',
                    icon: '🕉️', // Using Om symbol which is more reliable than SVG
                    special: showFireworks && (
                        <>
                            <div className="ganesh-pattern"></div>
                            <div className="modak-icon"></div>
                            <div className="lotus-pattern"></div>
                        </>
                    )
                };
            case 'Christmas':
                return {
                    background: 'linear-gradient(45deg, #D32F2F, #388E3C, #FFFFFF)',
                    textGradient: 'linear-gradient(to right, #FFEB3B, #FFFFFF)',
                    icon: '🎄', // Christmas tree
                    special: showFireworks && (
                        <>
                            <div className="snowflake" style={{ '--x': '20%', '--y': '30%' } as React.CSSProperties}></div>
                            <div className="snowflake" style={{ '--x': '50%', '--y': '60%' } as React.CSSProperties}></div>
                            <div className="snowflake" style={{ '--x': '80%', '--y': '40%' } as React.CSSProperties}></div>
                            <div className="christmas-lights"></div>
                        </>
                    )
                };
            case 'Dussehra':
                return {
                    background: 'linear-gradient(45deg, #FF4D4D, #FFD700, #FF8C00)',
                    textGradient: 'linear-gradient(to right, #FFD700, #FF4500)',
                    icon: '🏹', // Bow and arrow
                    special: showFireworks && (
                        <>
                            <div className="victory-sparkle" style={{ '--x': '30%', '--y': '50%' } as React.CSSProperties}></div>
                            <div className="victory-sparkle" style={{ '--x': '70%', '--y': '30%' } as React.CSSProperties}></div>
                            <div className="bow-arrow"></div>
                        </>
                    )
                };
            case 'Ramzan':
                return {
                    background: 'linear-gradient(45deg, #4CAF50, #2196F3, #1A237E)',
                    textGradient: 'linear-gradient(to right, #FFD700, #FFFFFF)',
                    icon: '🌙', // Crescent moon
                    special: showFireworks && (
                        <>
                            <div className="crescent-moon"></div>
                            <div className="star-pattern"></div>
                            <div className="lantern-glow"></div>
                        </>
                    )
                };
            case 'Ugadi':
                return {
                    background: 'linear-gradient(45deg, #FF9933, #4CAF50, #FFC300)',
                    textGradient: 'linear-gradient(to right, #FFD700, #228B22)',
                    icon: '🌕', // Ugadi moon
                    special: showFireworks && (
                        <>
                            <div className="ugadi-pattern"></div>
                            <div className="mango-leaves"></div>
                            <div className="neem-flowers"></div>
                            <div className="pachadi-bowl"></div>
                        </>
                    )
                };
            default:
                return {
                    background: 'linear-gradient(45deg, #3B82F6, #6366F1, #8B5CF6)',
                    textGradient: 'linear-gradient(to right, #FFFFFF, #E0E0E0)',
                    icon: '🎉',
                    special: null
                };
        }
    }, [currentFestival, showFireworks]);

    // Memoize the styles
    const styles = useMemo(() => getFestivalStyles(), [getFestivalStyles]);
    

    // if (!isDevelopment) return null;

    if (!currentFestival) return null;
    
    return (
        <div className="relative overflow-hidden rounded-2xl shadow-lg mb-6 sm:mb-8 z-50" key={key}>
            <div className="p-5 sm:p-6 lg:p-8 relative festival-banner-bg" style={{ 
                background: styles.background,
                backgroundSize: '200% 200%',
                animation: showFireworks ? 'gradientPosition 8s ease infinite' : 'none'
            }}>
                <div className="absolute inset-0 bg-white/10 transform -skew-x-12"></div>
                {styles.special}
                <div className="absolute inset-0">
                    <div className="absolute inset-0 opacity-20">
                        {showFireworks && (
                            <>
                                <div className="absolute top-0 left-0 w-8 h-8 sm:w-12 sm:h-12 bg-white/30 rounded-full animate-festival-particle-1"></div>
                                <div className="absolute top-1/2 left-1/4 w-6 h-6 sm:w-8 sm:h-8 bg-white/20 rounded-full animate-festival-particle-2"></div>
                                <div className="absolute bottom-0 right-1/3 w-8 h-8 sm:w-10 sm:h-10 bg-white/25 rounded-full animate-festival-particle-3"></div>
                            </>
                        )}
                    </div>
                </div>
                <div className="relative flex flex-col sm:flex-row items-center justify-between gap-4 sm:gap-6">
                    <div className="flex items-center space-x-4 sm:space-x-6 w-full sm:w-auto">
                        <div className={`bg-white/20 p-3 sm:p-4 lg:p-5 rounded-full shadow-inner backdrop-blur-sm flex-shrink-0 ${showFireworks ? 'animate-festival-icon' : ''}`}>
                            <span className="text-3xl sm:text-4xl lg:text-5xl">{currentFestival.icon}</span>
                        </div>
                        <div className={`flex-1 min-w-0 ${showFireworks ? 'animate-festival-text' : ''}`}>
                            <h3 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-2 sm:mb-3 leading-tight festival-gradient-text" style={{ 
                                background: styles.textGradient,
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                                textShadow: '0 2px 4px rgba(0,0,0,0.1)'
                            }}>Happy {currentFestival.name}!</h3>
                            <p className="text-white text-base sm:text-lg lg:text-xl mb-2 sm:mb-3 font-medium drop-shadow-md leading-relaxed">{currentFestival.message}</p>
                            <p className="text-white/90 text-sm sm:text-base lg:text-lg drop-shadow-sm leading-relaxed">{currentFestival.description}</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

// Also optimize the festival tester to prevent unnecessary re-renders
const DevFestivalTester = memo(() => {
    const [selectedFestival, setSelectedFestival] = useState<string>('');
    const festivals = useMemo(() => ['Holi', 'Diwali', 'Christmas', 'Ganesh Chaturthi', 'Dussehra', 'Ramzan', 'Ugadi'], []);

    const handleFestivalChange = useCallback((festival: string) => {
        setSelectedFestival(festival);
        setMockFestival(festival);
        // Use a more efficient way to trigger updates
        window.dispatchEvent(new CustomEvent('festival-changed', { detail: festival }));
    }, []);

    const clearFestival = useCallback(() => {
        setSelectedFestival('');
        setMockFestival(null);
        window.dispatchEvent(new CustomEvent('festival-changed', { detail: null }));
    }, []);

    // Only show festival tester in development for debugging
    if (import.meta.env.VITE_FLAVOR !== 'DEVELOPMENT') return null;

    return (
        <div className="mt-8 bg-white p-4 rounded-lg shadow-lg flex flex-col gap-2 max-w-md mx-auto">
            <h4 className="font-semibold text-gray-700 text-sm sm:text-base">Test Festival Banners</h4>
            <div className="flex flex-wrap gap-2">
                {festivals.map((festival) => (
                    <button
                        key={festival}
                        onClick={() => handleFestivalChange(festival)}
                        className={`px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm ${
                            selectedFestival === festival
                                ? 'bg-blue-500 text-white'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                    >
                        {festival}
                    </button>
                ))}
            </div>
            <button
                onClick={clearFestival}
                className="mt-2 px-3 py-1 bg-red-100 text-red-700 rounded-full text-xs sm:text-sm hover:bg-red-200"
            >
                Clear Festival
            </button>
        </div>
    );
});

export default function MyDashboard() {
    const navigate = useNavigate();
    const isMobile = useMediaQuery('(max-width: 768px)');
    const [showFestivalBanner, setShowFestivalBanner] = useState(false);
    const [currentFestival, setCurrentFestival] = useState(getCurrentFestival());
    const [, forceUpdate] = useState({});
    const [currentView, setCurrentView] = useState<DashboardView>(() => {
        // Load from localStorage, default to 'grid'
        const saved = localStorage.getItem('dashboard-view');
        return (saved as DashboardView) || 'compact';
    });
    
    console.log('currentView', currentView);
    // Get subscriptions for Chequered navigation
    const subscriptionList: SubscriptionDocWithId[] = useAppSelector((state: RootState) => state.subscriptionTableSlice?.data ?? []);
    const activeRole = useAppSelector(selectActiveRole);
    const isOwner = !activeRole?.role;
    const location = useLocation();
    
    // Force compact mode on mobile
    const effectiveView = isMobile ? 'compact' : currentView;
    
    // Handle Chequered tile click
    const handleChequeredClick = useCallback((e: React.MouseEvent) => {
        e.preventDefault();
        // If we have subscriptions, navigate to first one's chequered page
        if (subscriptionList.length > 0) {
            const firstSubscription = subscriptionList[0];
            navigate(`/chequered/${firstSubscription.id}`, { state: { from: location.pathname } });
        } else {
            // Otherwise navigate to subscriptions page
            navigate('/mysubscriptions');
        }
    }, [navigate, subscriptionList, location.pathname]);
    
    // Listen for festival changes
    useEffect(() => {
        const updateFestival = () => {
            const festival = getCurrentFestival();
            setCurrentFestival(festival);
            setShowFestivalBanner(!!festival);
        };

        // Update initially
        updateFestival();

        // Listen for festival changes
        window.addEventListener('festival-changed', updateFestival);

        return () => {
            window.removeEventListener('festival-changed', updateFestival);
        };
    }, []);

    const handleCTAClick = (e: React.MouseEvent) => {
        e.preventDefault();
        navigate('/events-offers');
    };

    // Force update when festival changes
    useEffect(() => {
        forceUpdate({});
    }, [currentFestival?.name]);

    const calendlyLink = "https://calendly.com/aditya-khambampati/30min?back=1&month=2025-04&date=2025-04-15";

    const openCalendly = () => {
        window.open(calendlyLink, '_blank');
    };

    // Handle dashboard view change and persist to localStorage
    const handleViewChange = (view: DashboardView) => {
        setCurrentView(view);
        localStorage.setItem('dashboard-view', view);
    };

    const navigationTiles = [
        {
            title: "Subscriptions",
            description: "Manage all your batches and groups!",
            icon: (
                <svg className="size-8" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
                    <path d="M15 2H9a1 1 0 0 0-1 1v2a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V3a1 1 0 0 0-1-1z" />
                    <path d="M8 10h8" /><path d="M8 14h8" /><path d="M8 18h8" />
                </svg>
            ),
            image: "https://firebasestorage.googleapis.com/v0/b/coachprodev.appspot.com/o/Config%2FBusiness%20Plan-bro.png?alt=media&token=f1e721a6-1e18-41fe-b0b8-7419aaefe4f3",
            path: "/mysubscriptions"
        },
        {
            title: "Events & Offers",
            description: "Manage your events and special offers",
            icon: (
                <svg className="size-8 text-blue-500" data-name="ticket cut" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 48 48">
                    <path fill="currentColor" d="M40 8H27a1 1 0 0 0-1 1 2 2 0 0 1-4 0 1 1 0 0 0-1-1H8a4 4 0 0 0-4 4v17a4 4 0 0 0 4 4h14.56l.29.5-.54.94A3.46 3.46 0 0 0 19.5 33a3.5 3.5 0 1 0 3.32 4.56l.05-.06 1.13-2 1.13 2 .05.06A3.49 3.49 0 1 0 28.5 33a3.46 3.46 0 0 0-2.81 1.44l-.54-.94.29-.5H40a4 4 0 0 0 4-4V12a4 4 0 0 0-4-4zM19.5 38a1.5 1.5 0 1 1 1.5-1.5 1.5 1.5 0 0 1-1.5 1.5zm9-3a1.5 1.5 0 1 1-1.5 1.5 1.5 1.5 0 0 1 1.5-1.5zM42 29a2 2 0 0 1-2 2H26.58l4.29-7.5a1 1 0 0 0-1.74-1L24.28 31H8a2 2 0 0 1-2-2V12a2 2 0 0 1 2-2h12.13a4 4 0 0 0 7.74 0H40a2 2 0 0 1 2 2z"/>
                    <circle fill="currentColor" cx="24" cy="14" r="1"/>
                    <circle fill="currentColor" cx="24" cy="20" r="1"/>
                    <circle fill="currentColor" cx="24" cy="29" r="1"/>
                    <circle fill="currentColor" cx="24" cy="23" r="1"/>
                    <circle fill="currentColor" cx="24" cy="26" r="1"/>
                    <circle fill="currentColor" cx="24" cy="17" r="1"/>
                    <path fill="currentColor" d="M18.5 22a2.5 2.5 0 1 0 2.5 2.5 2.5 2.5 0 0 0-2.5-2.5zm0 3a.5.5 0 0 1-.5-.5.5.5 0 0 1 1 0 .5.5 0 0 1-.5.5zM11.5 20A2.5 2.5 0 1 0 9 17.5a2.5 2.5 0 0 0 2.5 2.5zm0-3a.5.5 0 0 1 .5.5.5.5 0 0 1-1 0 .5.5 0 0 1 .5-.5zM19.29 15.29l-10 10a1 1 0 1 0 1.42 1.41l10-10a1 1 0 0 0 0-1.41 1 1 0 0 0-1.42 0zM39 12h-9a1 1 0 0 0-1 1v5a1 1 0 0 0 1 1h9a1 1 0 0 0 1-1v-5a1 1 0 0 0-1-1zm-1 5h-7v-3h7zM39 21h-5a1 1 0 0 0 0 2h5a1 1 0 0 0 0-2zM39 25h-5a1 1 0 0 0 0 2h5a1 1 0 0 0 0-2z"/>
                </svg>
            ),
            image: "https://firebasestorage.googleapis.com/v0/b/coachprodev.appspot.com/o/Config%2FEvents-rafiki.png?alt=media&token=acf01730-ec92-47ff-bce5-ab54b00085f8",
            path: "/events-offers"
        },
        // {
        //     title: "Storefront",
        //     description: "Manage your online store",
        //     icon: (
        //         <svg className="size-8" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        //             <path d="m2 7 4.41-4.41A2 2 0 0 1 7.83 2h8.34a2 2 0 0 1 1.42.59L22 7" />
        //             <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
        //             <path d="M15 22v-4a2 2 0 0 0-2-2h-2a2 2 0 0 0-2 2v4" />
        //             <path d="M2 7h20" />
        //         </svg>
        //     ),
        //     image: "https://firebasestorage.googleapis.com/v0/b/coachprodev.appspot.com/o/Config%2Fcake%20shop-bro.png?alt=media&token=19f8f60c-52ad-4137-8128-97760c40f992",
        //     path: "/storefront"
        // },
        {
            title: "My World",
            description: "Personal attendance tracking and calendar view",
            icon: (
                <svg className="size-8" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M8 2v4" />
                    <path d="M16 2v4" />
                    <path d="M21 6H3a1 1 0 0 0-1 1v14a1 1 0 0 0 1 1h18a1 1 0 0 0 1-1V7a1 1 0 0 0-1-1z" />
                    <path d="M3 10h18" />
                    <path d="M8 14h.01" />
                    <path d="M12 14h.01" />
                    <path d="M16 14h.01" />
                    <path d="M8 18h.01" />
                    <path d="M12 18h.01" />
                    <path d="M16 18h.01" />
                </svg>
            ),
            image: "https://img.freepik.com/free-vector/schedule-concept-illustration_114360-1432.jpg",
            path: "/myworld"
        },
    
        {
            title: "Inflow",
            description: "Manage and track your leads through different stages",
            icon: (
                <svg className="size-8" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
                    <circle cx="9" cy="7" r="4"></circle>
                    <path d="M22 21v-2a4 4 0 0 0-3-3.87"></path>
                    <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                </svg>
            ),
            image: "https://img.freepik.com/free-vector/crm-system-abstract-concept_335657-3039.jpg",
            path: "/inflow"
        },
        ...([{
            title: "Ledger",
            description: "View your complete financial ledger",
            icon: (
                <svg className="size-8" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
                    <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
                    <path d="M8 7h8" />
                    <path d="M8 11h8" />
                    <path d="M8 15h4" />
                </svg>
            ),
            image: "https://img.freepik.com/free-vector/accounting-concept-illustration_114360-4991.jpg",
            path: "/ledger"
        }]),
        {
            title: "Export Data",
            description: "Export and manage your data",
            icon: (
                <svg className="size-8" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="7 10 12 15 17 10" />
                    <line x1="12" y1="15" x2="12" y2="3" />
                </svg>
            ),
            image: "https://img.freepik.com/free-vector/data-report-illustration-concept_114360-883.jpg",
            path: "/exports"
        },
        // {
        //     title: "Chequered",
        //     description: "Chess teaching and puzzle management platform",
        //     icon: (
        //         <svg className="size-8" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        //             <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
        //             <path d="M3 9h18M9 3v18" />
        //             <path d="M9 9h12v12" />
        //         </svg>
        //     ),
        //     image: "https://images.unsplash.com/photo-1529699211952-734e80c4d42b?auto=format&fit=crop&w=1600&q=80",
        //     path: "/mysubscriptions",
        //     isChequered: true
        // },
        {
            title: "Settings",
            description: "Manage your account settings",
            icon: (
                <svg className="size-8" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="18" cy="15" r="3" />
                    <circle cx="9" cy="7" r="4" />
                    <path d="M10 15H6a4 4 0 0 0-4 4v2" />
                    <path d="m21.7 16.4-.9-.3" />
                    <path d="m15.2 13.9-.9-.3" />
                    <path d="m16.6 18.7.3-.9" />
                    <path d="m19.1 12.2.3-.9" />
                    <path d="m19.6 18.7-.4-1" />
                    <path d="m16.8 12.3-.4-1" />
                    <path d="m14.3 16.6 1-.4" />
                    <path d="m20.7 13.8 1-.4" />
                </svg>
            ),
            image: "https://firebasestorage.googleapis.com/v0/b/coachprodev.appspot.com/o/Config%2FSettings-amico.png?alt=media&token=e6be8a74-221f-4e3a-924e-b506b56f1cb8",
            path: "/settings"
        },
        {
            title: "Manage Academies",
            description: "Create and manage your academies",
            icon: (
                <svg className="size-8" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 10v6M2 10l10-5 10 5-10 5z" />
                    <path d="M6 12v5c3 3 9 3 12 0v-5" />
                </svg>
            ),
            image: "https://img.freepik.com/free-vector/university-concept-illustration_114360-7620.jpg",
            path: "/superadmin",
            ownerOnly: true
        }
    ];

    // Update the pulseStyle to use blue tones
    const pulseStyle = {
        animation: 'pulse 2s infinite',
        boxShadow: '0 0 0 rgba(59, 130, 246, 0.6)', // Blue boxShadow
        position: 'relative' as const
    };

    // Update the keyframes animation to use blue colors
    useEffect(() => {
        const styleEl = document.createElement('style');
        styleEl.innerHTML = `
            @keyframes float {
                0%, 100% { transform: translateY(0px); }
                50% { transform: translateY(-10px); }
            }

            @keyframes shine {
                0% { background-position: 200% center; }
                100% { background-position: -200% center; }
            }

            @keyframes pulse {
                0% { box-shadow: 0 0 0 0 rgba(59, 130, 246, 0.6); }
                70% { box-shadow: 0 0 0 10px rgba(59, 130, 246, 0); }
                100% { box-shadow: 0 0 0 0 rgba(59, 130, 246, 0); }
            }

            @keyframes gradientPosition {
                0% { background-position: 0% 50%; }
                50% { background-position: 100% 50%; }
                100% { background-position: 0% 50%; }
            }

            @keyframes festival-particle-1 {
                0%, 100% { transform: translate(0, 0) scale(1); }
                50% { transform: translate(20px, 20px) scale(1.2); }
            }

            @keyframes festival-particle-2 {
                0%, 100% { transform: translate(0, 0) scale(1.1); }
                50% { transform: translate(-15px, -15px) scale(0.9); }
            }

            @keyframes festival-particle-3 {
                0%, 100% { transform: translate(0, 0) scale(0.9); }
                50% { transform: translate(25px, -25px) scale(1.1); }
            }

            @keyframes festival-icon {
                0%, 100% { transform: rotate(0deg) scale(1); }
                25% { transform: rotate(10deg) scale(1.1); }
                75% { transform: rotate(-10deg) scale(0.95); }
            }

            @keyframes festival-text {
                0% { transform: translateX(-10px); opacity: 0; }
                100% { transform: translateX(0); opacity: 1; }
            }

            @keyframes firework-explosion {
                0% { transform: scale(0); opacity: 1; }
                50% { transform: scale(1); opacity: 0.8; }
                100% { transform: scale(1.2); opacity: 0; }
            }

            @keyframes color-splash {
                0% { transform: scale(0) rotate(0deg); opacity: 0; }
                50% { transform: scale(1.2) rotate(180deg); opacity: 0.8; }
                100% { transform: scale(1.5) rotate(360deg); opacity: 0; }
            }

            @keyframes diya-flicker {
                0%, 100% { transform: scale(1); opacity: 0.8; }
                50% { transform: scale(1.1); opacity: 1; }
            }

            @keyframes sparkle {
                0%, 100% { opacity: 0; transform: scale(0); }
                50% { opacity: 1; transform: scale(1); }
            }

            @keyframes rainbow-move {
                0% { background-position: 0% 50%; }
                100% { background-position: 100% 50%; }
            }

            @keyframes icon-spin {
                from { transform: rotate(0deg); }
                to { transform: rotate(360deg); }
            }

            .firework {
                position: absolute;
                width: 100px;
                height: 100px;
                left: var(--x);
                top: var(--y);
                background: radial-gradient(circle, #FFD700, #FFA500, transparent 70%);
                border-radius: 50%;
                animation: firework-explosion 1.5s ease-out infinite;
                mix-blend-mode: screen;
            }

            .firework::before,
            .firework::after {
                content: '';
                position: absolute;
                width: 100%;
                height: 100%;
                background: inherit;
                border-radius: inherit;
                animation: inherit;
                transform: rotate(45deg);
            }

            .firework::after {
                transform: rotate(-45deg);
            }

            .color-splash {
                position: absolute;
                width: 80px;
                height: 80px;
                left: var(--x);
                top: var(--y);
                background: var(--color);
                border-radius: 50%;
                filter: blur(10px);
                animation: color-splash 2s ease-out infinite;
                mix-blend-mode: screen;
                opacity: 0.8;
            }

            .diya-flame {
                position: absolute;
                bottom: 20px;
                right: 20px;
                width: 30px;
                height: 30px;
                background: radial-gradient(circle at center, #FFD700, #FFA500);
                border-radius: 50% 50% 50% 50% / 60% 60% 40% 40%;
                animation: diya-flicker 1s ease-in-out infinite;
                filter: blur(2px);
                box-shadow: 0 0 20px #FFA500;
            }

            .sparkles {
                position: absolute;
                inset: 0;
                pointer-events: none;
            }

            .sparkles::before,
            .sparkles::after {
                content: '';
                position: absolute;
                width: 4px;
                height: 4px;
                border-radius: 50%;
                background: #FFD700;
                animation: sparkle 1s ease-in-out infinite;
                box-shadow: 0 0 4px #FFD700;
            }

            .rainbow-overlay {
                position: absolute;
                inset: 0;
                background: linear-gradient(
                    45deg,
                    rgba(255,0,0,0.2),
                    rgba(255,165,0,0.2),
                    rgba(255,255,0,0.2),
                    rgba(0,128,0,0.2),
                    rgba(0,0,255,0.2),
                    rgba(75,0,130,0.2),
                    rgba(238,130,238,0.2)
                );
                background-size: 200% 200%;
                animation: rainbow-move 3s linear infinite;
                mix-blend-mode: overlay;
            }

            .ganesh-pattern {
                position: absolute;
                inset: 0;
                background-image: url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M20 0c11.046 0 20 8.954 20 20s-8.954 20-20 20S0 31.046 0 20 8.954 0 20 0z' fill='rgba(255,255,255,0.1)'/%3E%3C/svg%3E");
                background-size: 40px 40px;
                opacity: 0.3;
            }

            .modak-icon {
                position: absolute;
                right: 20px;
                bottom: 20px;
                width: 40px;
                height: 40px;
                background: rgba(255, 255, 255, 0.2);
                border-radius: 50%;
                animation: float 3s ease-in-out infinite;
            }

            .lotus-pattern {
                position: absolute;
                left: 20px;
                top: 20px;
                width: 60px;
                height: 60px;
                background: rgba(255, 255, 255, 0.15);
                clip-path: polygon(50% 0%, 80% 30%, 100% 50%, 80% 70%, 50% 100%, 20% 70%, 0% 50%, 20% 30%);
                animation: rotate 10s linear infinite;
            }

            .snowflake {
                position: absolute;
                width: 20px;
                height: 20px;
                left: var(--x);
                top: var(--y);
                background: rgba(255, 255, 255, 0.8);
                clip-path: polygon(50% 0%, 65% 35%, 100% 50%, 65% 65%, 50% 100%, 35% 65%, 0% 50%, 35% 35%);
                animation: snowfall 4s linear infinite;
            }

            .christmas-lights {
                position: absolute;
                bottom: 0;
                left: 0;
                right: 0;
                height: 4px;
                background: linear-gradient(90deg, #D32F2F 25%, #388E3C 25%, #388E3C 50%, #D32F2F 50%, #D32F2F 75%, #388E3C 75%);
                background-size: 40px 4px;
                animation: lights-twinkle 1s linear infinite;
            }

            .victory-sparkle {
                position: absolute;
                width: 30px;
                height: 30px;
                left: var(--x);
                top: var(--y);
                background: radial-gradient(circle, #FFD700, transparent 70%);
                border-radius: 50%;
                animation: victory-pulse 2s ease-in-out infinite;
            }

            .bow-arrow {
                position: absolute;
                right: 30px;
                top: 50%;
                width: 40px;
                height: 2px;
                background: rgba(255, 255, 255, 0.6);
                transform: translateY(-50%);
                animation: arrow-shoot 3s ease-in-out infinite;
            }

            .crescent-moon {
                position: absolute;
                right: 30px;
                top: 30px;
                width: 30px;
                height: 30px;
                background: rgba(255, 255, 255, 0.2);
                border-radius: 50%;
                box-shadow: 5px -5px 0 rgba(255, 255, 255, 0.4);
                animation: moon-glow 3s ease-in-out infinite;
            }

            .star-pattern {
                position: absolute;
                inset: 0;
                background-image: radial-gradient(circle, rgba(255,255,255,0.2) 1px, transparent 1px);
                background-size: 20px 20px;
                animation: twinkle 2s ease-in-out infinite;
            }

            .lantern-glow {
                position: absolute;
                left: 30px;
                bottom: 30px;
                width: 20px;
                height: 30px;
                background: radial-gradient(circle at center, rgba(255,215,0,0.4), transparent 70%);
                animation: lantern-float 3s ease-in-out infinite;
            }

            @keyframes snowfall {
                0% { transform: translateY(-20px) rotate(0deg); opacity: 0; }
                50% { opacity: 1; }
                100% { transform: translateY(20px) rotate(180deg); opacity: 0; }
            }

            @keyframes lights-twinkle {
                0%, 100% { opacity: 1; }
                50% { opacity: 0.7; }
            }

            @keyframes victory-pulse {
                0%, 100% { transform: scale(1); opacity: 0.8; }
                50% { transform: scale(1.2); opacity: 0.4; }
            }

            @keyframes arrow-shoot {
                0% { transform: translateX(0) translateY(-50%); }
                50% { transform: translateX(-20px) translateY(-50%); }
                100% { transform: translateX(0) translateY(-50%); }
            }

            @keyframes moon-glow {
                0%, 100% { opacity: 0.8; transform: scale(1); }
                50% { opacity: 1; transform: scale(1.1); }
            }

            @keyframes twinkle {
                0%, 100% { opacity: 0.4; }
                50% { opacity: 0.8; }
            }

            @keyframes lantern-float {
                0%, 100% { transform: translateY(0); }
                50% { transform: translateY(-10px); }
            }

            .festival-banner-bg {
                position: relative;
                overflow: hidden;
            }

            .festival-banner-bg::before {
                content: '';
                position: absolute;
                inset: 0;
                background: inherit;
                filter: blur(10px);
                transform: scale(1.1);
                z-index: -1;
            }

            .animate-festival-particle-1 {
                animation: festival-particle-1 8s ease-in-out infinite;
            }

            .animate-festival-particle-2 {
                animation: festival-particle-2 12s ease-in-out infinite;
            }

            .animate-festival-particle-3 {
                animation: festival-particle-3 10s ease-in-out infinite;
            }

            .animate-festival-icon {
                animation: festival-icon 4s ease-in-out infinite;
            }

            .animate-festival-text {
                animation: festival-text 0.8s ease-out forwards;
            }
            
            .demo-button {
                transition: all 0.3s ease;
                background: linear-gradient(135deg, #3b82f6, #1e40af) !important;
                position: relative;
                overflow: hidden;
            }
            
            .demo-button:hover {
                transform: translateY(-2px);
                background: linear-gradient(135deg, #2563eb, #1e3a8a) !important;
            }

            .demo-button::after {
                content: '';
                position: absolute;
                top: -50%;
                left: -50%;
                width: 200%;
                height: 200%;
                background: linear-gradient(
                    45deg,
                    transparent,
                    rgba(255, 255, 255, 0.1),
                    transparent
                );
                transform: rotate(45deg);
                transition: all 0.3s ease;
            }

            .demo-button:hover::after {
                animation: shine 1.5s infinite;
            }
            
            .dashboard-tile {
                transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
                background: linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.98) 100%);
                backdrop-filter: blur(10px);
                border: 1px solid rgba(59, 130, 246, 0.1);
                position: relative;
                overflow: hidden;
                isolation: isolate;
                cursor: pointer;
            }
            
            .dashboard-tile::before {
                content: '';
                position: absolute;
                inset: 0;
                background: radial-gradient(
                    circle at var(--mouse-x, 50%) var(--mouse-y, 50%),
                    rgba(255, 255, 255, 0.1) 0%,
                    transparent 50%
                );
                opacity: 0;
                transition: opacity 0.3s;
                z-index: -1;
            }
            
            .dashboard-tile:hover::before {
                opacity: 1;
            }
            
            .dashboard-tile::after {
                content: '';
                position: absolute;
                inset: 0;
                background: linear-gradient(
                    120deg,
                    transparent 20%,
                    rgba(255, 255, 255, 0.1),
                    transparent 80%
                );
                transform: translateX(-100%);
                transition: transform 0.6s;
                z-index: -1;
            }
            
            .dashboard-tile:hover {
                transform: translateY(-8px) scale(1.03);
                box-shadow: 
                    0 25px 50px rgba(0, 0, 0, 0.15),
                    0 0 30px rgba(59, 130, 246, 0.3),
                    inset 0 0 0 1px rgba(59, 130, 246, 0.2),
                    0 10px 20px rgba(139, 92, 246, 0.1);
                border-color: rgba(59, 130, 246, 0.3);
            }
            
            .dashboard-tile:hover::after {
                transform: translateX(100%);
            }

            .tile-icon {
                transition: all 0.4s ease;
                position: relative;
                background: transparent;
            }
            
            .tile-icon::before {
                content: '';
                position: absolute;
                inset: -4px;
                background: conic-gradient(
                    from 0deg,
                    rgba(59, 130, 246, 0.15),
                    rgba(99, 102, 241, 0.15),
                    rgba(139, 92, 246, 0.15),
                    rgba(59, 130, 246, 0.15)
                );
                border-radius: inherit;
                animation: rotate 4s linear infinite;
                opacity: 0;
                transition: opacity 0.3s;
            }
            
            @keyframes rotate {
                from { transform: rotate(0deg); }
                to { transform: rotate(360deg); }
            }
            
            .dashboard-tile:hover .tile-icon::before {
                opacity: 1;
            }

            .dashboard-tile:hover .tile-icon {
                transform: scale(1.15) rotate(12deg);
                box-shadow: 0 8px 16px rgba(59, 130, 246, 0.3);
            }

            .dashboard-tile:hover .tile-icon svg,
            .dashboard-tile:hover .tile-icon img {
                filter: drop-shadow(0 2px 4px rgba(59, 130, 246, 0.3));
            }

            @keyframes counter-spin {
                from { transform: rotate(0deg); }
                to { transform: rotate(-360deg); }
            }

            .banner-gradient {
                background: linear-gradient(135deg, #3b82f6 0%, #6366f1 50%, #8b5cf6 100%);
                animation: gradientFlow 8s ease infinite;
                background-size: 200% 200%;
            }

            @keyframes gradientFlow {
                0% { background-position: 0% 50%; }
                50% { background-position: 100% 50%; }
                100% { background-position: 0% 50%; }
            }

            @keyframes gradientShift {
                0%, 100% { background-position: 0% 50%; }
                50% { background-position: 100% 50%; }
            }

            .banner-hover {
                transition: all 0.4s ease;
            }

            .banner-hover:hover {
                transform: translateY(-2px) scale(1.01);
                box-shadow: 0 12px 30px rgba(0, 0, 0, 0.2);
            }

            .welcome-text {
                background: linear-gradient(135deg, #1e40af 0%, #3b82f6 50%, #60a5fa 100%);
                -webkit-background-clip: text;
                -webkit-text-fill-color: transparent;
                animation: gradientFlow 8s ease infinite;
                background-size: 200% 200%;
            }

            .floating-image {
                animation: float 6s ease-in-out infinite;
                perspective: 1000px;
                transform-style: preserve-3d;
            }

            .dashboard-tile:hover .floating-image {
                animation: none;
                transform: 
                    perspective(1000px)
                    rotateY(15deg)
                    rotateX(5deg)
                    scale(1.1);
                transition: transform 0.6s ease-out;
            }

            .grid-background {
                background-size: 30px 30px;
                background-image: 
                    linear-gradient(to right, rgba(59, 130, 246, 0.05) 1px, transparent 1px),
                    linear-gradient(to bottom, rgba(59, 130, 246, 0.05) 1px, transparent 1px);
            }

            /* Special styling for My World tile */
            .my-world-special {
                background: linear-gradient(135deg, rgba(147, 51, 234, 0.05) 0%, rgba(236, 72, 153, 0.05) 100%);
                border: 2px solid transparent;
                background-clip: padding-box;
                position: relative;
            }

            .my-world-special::before {
                content: '';
                position: absolute;
                inset: 0;
                border-radius: inherit;
                padding: 2px;
                background: linear-gradient(135deg, #9333ea, #ec4899);
                -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
                -webkit-mask-composite: xor;
                mask-composite: exclude;
                pointer-events: none;
                opacity: 0.4;
                transition: opacity 0.3s;
            }

            .my-world-special:hover::before {
                opacity: 1;
            }

            .my-world-special:hover {
                transform: translateY(-6px) scale(1.03);
                box-shadow: 
                    0 25px 50px rgba(147, 51, 234, 0.25),
                    0 0 30px rgba(236, 72, 153, 0.2),
                    inset 0 0 0 1px rgba(147, 51, 234, 0.2);
            }

            .ugadi-pattern {
                position: absolute;
                inset: 0;
                background-image: url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M20 0c11.046 0 20 8.954 20 20s-8.954 20-20 20S0 31.046 0 20 8.954 0 20 0z' fill='rgba(255,255,255,0.1)'/%3E%3C/svg%3E");
                background-size: 40px 40px;
                opacity: 0.3;
                animation: ugadi-pattern-rotate 20s linear infinite;
            }

            .mango-leaves {
                position: absolute;
                right: 20px;
                top: 20px;
                width: 40px;
                height: 40px;
                background: rgba(76, 175, 80, 0.3);
                clip-path: polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%);
                animation: mango-leaves-float 4s ease-in-out infinite;
            }

            .neem-flowers {
                position: absolute;
                left: 30px;
                bottom: 30px;
                width: 20px;
                height: 20px;
                background: rgba(255, 255, 255, 0.4);
                border-radius: 50%;
                box-shadow: 
                    15px -15px 0 rgba(255, 255, 255, 0.4),
                    -15px -15px 0 rgba(255, 255, 255, 0.4),
                    15px 15px 0 rgba(255, 255, 255, 0.4),
                    -15px 15px 0 rgba(255, 255, 255, 0.4);
                animation: neem-flowers-twinkle 2s ease-in-out infinite;
            }

            .pachadi-bowl {
                position: absolute;
                right: 40px;
                bottom: 20px;
                width: 30px;
                height: 15px;
                background: rgba(255, 255, 255, 0.3);
                border-radius: 15px 15px 5px 5px;
                animation: pachadi-float 3s ease-in-out infinite;
            }

            @keyframes ugadi-pattern-rotate {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
            }

            @keyframes mango-leaves-float {
                0%, 100% { transform: translateY(0) rotate(0deg); }
                50% { transform: translateY(-10px) rotate(5deg); }
            }

            @keyframes neem-flowers-twinkle {
                0%, 100% { opacity: 0.4; transform: scale(1); }
                50% { opacity: 0.8; transform: scale(1.1); }
            }

            @keyframes pachadi-float {
                0%, 100% { transform: translateY(0) rotate(-2deg); }
                50% { transform: translateY(-5px) rotate(2deg); }
            }
        `;
        
        document.head.appendChild(styleEl);
        
        // Add mouse move event listener for the spotlight effect
        const handleMouseMove = (e: MouseEvent) => {
            const tiles = document.querySelectorAll('.dashboard-tile');
            tiles.forEach((tile: Element) => {
                const rect = (tile as HTMLElement).getBoundingClientRect();
                const x = e.clientX - rect.left;
                const y = e.clientY - rect.top;
                (tile as HTMLElement).style.setProperty('--mouse-x', `${x}px`);
                (tile as HTMLElement).style.setProperty('--mouse-y', `${y}px`);
            });
        };

        document.addEventListener('mousemove', handleMouseMove);
        
        return () => {
            if (styleEl && document.head.contains(styleEl)) {
                document.head.removeChild(styleEl);
            }
            document.removeEventListener('mousemove', handleMouseMove);
        };
    }, []);

    return (
        <main className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 grid-background">
            <div className="container mx-auto px-4 py-8 space-y-8">
                {/* Show either Festival Banner or Campaign Banner, not both */}
                {showFestivalBanner ? (
                    <FestivalBanner key={currentFestival?.name || 'no-festival'} />
                ) : (
                    <Link to="/events-offers" className="block">
                        <div className="banner-gradient banner-hover relative py-4 sm:py-6 lg:py-8 px-4 sm:px-6 lg:px-8 rounded-xl sm:rounded-2xl shadow-lg">
                            <div className="absolute inset-0 bg-white/10 transform -skew-x-12 rounded-xl sm:rounded-2xl"></div>
                            <div className="flex flex-col sm:flex-row items-center justify-between text-white gap-3 sm:gap-4">
                                <div className="flex items-center space-x-3 sm:space-x-4 lg:space-x-6 w-full sm:w-auto">
                                    <div className="bg-white/20 p-2 sm:p-3 lg:p-4 rounded-full shadow-inner flex-shrink-0">
                                        <WhatsAppIcon className="text-2xl sm:text-3xl lg:text-4xl animate-bounce" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="font-bold text-base sm:text-xl lg:text-2xl mb-1.5 sm:mb-2 lg:mb-3 leading-tight">Have you tried our Events yet?!</p>
                                        <p className="font-medium text-white/90 text-xs sm:text-sm lg:text-base leading-relaxed">Attract more customers and supercharge your revenue growth by crafting the perfect events!</p>
                                    </div>
                                </div>
                                <Button
                                    variant="contained"
                                    className="w-full sm:w-auto sm:ml-4 bg-white/95 hover:bg-white text-blue-600 font-semibold shadow-lg hover:shadow-xl transition-all duration-300 px-4 sm:px-6 lg:px-8 py-2 sm:py-2.5 lg:py-3 text-xs sm:text-sm lg:text-base rounded-lg sm:rounded-xl whitespace-nowrap"
                                    onClick={handleCTAClick}
                                >
                                    Try Now
                                </Button>
                            </div>
                        </div>
                    </Link>
                )}

                {/* Dashboard View Selector - Hidden on mobile */}
                {!isMobile && (
                    <DashboardViewSelector 
                        currentView={currentView} 
                        onViewChange={handleViewChange} 
                    />
                )}

                {/* Dynamic Varied Layout - Different Sizes Like Chequered */}
                <motion.section 
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, ease: [0.4, 0, 0.2, 1] }}
                    className={`${currentFestival ? 'mt-4' : ''} relative`}
                    style={{
                        display: 'grid',
                        gridTemplateColumns: effectiveView === 'grid' 
                            ? 'repeat(auto-fit, minmax(280px, 1fr))'
                            : isMobile
                                ? 'repeat(2, 1fr)' // Mobile: 2 columns
                                : 'repeat(auto-fill, minmax(160px, 1fr))', // Desktop compact: flexible columns
                        gap: effectiveView === 'grid' 
                            ? (isMobile ? '1rem' : '1.5rem')
                            : (isMobile ? '0.75rem' : '1rem'),
                        gridAutoRows: effectiveView === 'grid' ? 'masonry' : 'auto',
                        gridAutoFlow: effectiveView === 'grid' ? 'row dense' : 'row',
                        padding: isMobile ? '0.5rem' : '1rem',
                        maxWidth: '100%',
                        width: '100%'
                    }}
                >
                    {/* Animated background gradient */}
                    <div className="absolute inset-0 -z-10 opacity-30"
                        style={{
                            background: 'radial-gradient(circle at 20% 50%, rgba(59, 130, 246, 0.1) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(139, 92, 246, 0.1) 0%, transparent 50%), radial-gradient(circle at 40% 20%, rgba(236, 72, 153, 0.1) 0%, transparent 50%)',
                            animation: 'gradientShift 15s ease infinite',
                            backgroundSize: '200% 200%'
                        }}
                    ></div>

                    {navigationTiles.filter(tile => !('ownerOnly' in tile) || isOwner).map((tile, index) => {
                        // Generate unique gradient colors for each tile
                        const gradients = [
                            'from-blue-500/20 via-indigo-500/20 to-purple-500/20',
                            'from-purple-500/20 via-pink-500/20 to-red-500/20',
                            'from-emerald-500/20 via-teal-500/20 to-cyan-500/20',
                            'from-orange-500/20 via-amber-500/20 to-yellow-500/20',
                            'from-rose-500/20 via-pink-500/20 to-fuchsia-500/20',
                            'from-violet-500/20 via-purple-500/20 to-indigo-500/20',
                            'from-cyan-500/20 via-blue-500/20 to-indigo-500/20',
                            'from-green-500/20 via-emerald-500/20 to-teal-500/20'
                        ];
                        const tileGradient = gradients[index % gradients.length];
                        
                        // Create varied sizes - some tiles are larger, some smaller
                        // For compact mode, use uniform sizes for better alignment
                        const sizeVariations = effectiveView === 'grid' 
                            ? [
                                { gridColumn: 'span 1', gridRow: 'span 1', minHeight: '200px' }, // Normal
                                { gridColumn: 'span 1', gridRow: 'span 2', minHeight: '280px' }, // Tall
                                { gridColumn: 'span 2', gridRow: 'span 1', minHeight: '180px' }, // Wide
                                { gridColumn: 'span 1', gridRow: 'span 1', minHeight: '220px' }, // Normal
                                { gridColumn: 'span 1', gridRow: 'span 1', minHeight: '190px' }, // Normal
                                { gridColumn: 'span 2', gridRow: 'span 2', minHeight: '320px' }, // Large
                                { gridColumn: 'span 1', gridRow: 'span 1', minHeight: '200px' }, // Normal
                                { gridColumn: 'span 1', gridRow: 'span 1', minHeight: '210px' }  // Normal
                            ]
                            : isMobile
                                ? [
                                    // Mobile compact: uniform square tiles, 2 columns
                                    { gridColumn: 'span 1', gridRow: 'span 1', minHeight: '140px' },
                                    { gridColumn: 'span 1', gridRow: 'span 1', minHeight: '140px' },
                                    { gridColumn: 'span 1', gridRow: 'span 1', minHeight: '140px' },
                                    { gridColumn: 'span 1', gridRow: 'span 1', minHeight: '140px' },
                                    { gridColumn: 'span 1', gridRow: 'span 1', minHeight: '140px' },
                                    { gridColumn: 'span 1', gridRow: 'span 1', minHeight: '140px' },
                                    { gridColumn: 'span 1', gridRow: 'span 1', minHeight: '140px' },
                                    { gridColumn: 'span 1', gridRow: 'span 1', minHeight: '140px' }
                                ]
                                : [
                                    // Desktop compact: slightly varied but aligned
                                    { gridColumn: 'span 1', gridRow: 'span 1', minHeight: '160px' },
                                    { gridColumn: 'span 1', gridRow: 'span 1', minHeight: '160px' },
                                    { gridColumn: 'span 1', gridRow: 'span 1', minHeight: '160px' },
                                    { gridColumn: 'span 1', gridRow: 'span 1', minHeight: '160px' },
                                    { gridColumn: 'span 1', gridRow: 'span 1', minHeight: '160px' },
                                    { gridColumn: 'span 1', gridRow: 'span 1', minHeight: '160px' },
                                    { gridColumn: 'span 1', gridRow: 'span 1', minHeight: '160px' },
                                    { gridColumn: 'span 1', gridRow: 'span 1', minHeight: '160px' }
                                ];
                        
                        const sizeConfig = sizeVariations[index % sizeVariations.length];
                        const isLarge = effectiveView === 'grid' && sizeConfig.gridColumn === 'span 2' && sizeConfig.gridRow === 'span 2';
                        const isTall = effectiveView === 'grid' && sizeConfig.gridRow === 'span 2' && sizeConfig.gridColumn === 'span 1';
                        const isWide = effectiveView === 'grid' && sizeConfig.gridColumn === 'span 2' && sizeConfig.gridRow === 'span 1';
                        const Wrapper: React.ElementType = tile.isChequered ? 'div' : Link;
                        const wrapperClassName = `dashboard-tile group relative block rounded-xl overflow-hidden transition-all duration-500 ${
                            isLarge ? 'p-8' : isTall ? 'p-6' : isWide ? 'p-5' : effectiveView === 'grid' 
                                ? 'p-6' 
                                : isMobile 
                                    ? 'p-3' 
                                    : 'p-4'
                        } ${tile.title === "My World" ? "my-world-special" : ""}`;
                        const wrapperStyle = {
                            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.7) 0%, rgba(255, 255, 255, 0.4) 100%)',
                            backdropFilter: 'blur(20px) saturate(180%)',
                            WebkitBackdropFilter: 'blur(20px) saturate(180%)',
                            border: '1px solid rgba(255, 255, 255, 0.3)',
                            boxShadow: `
                                0 8px 32px rgba(0, 0, 0, 0.1),
                                0 2px 8px rgba(0, 0, 0, 0.05),
                                inset 0 1px 0 rgba(255, 255, 255, 0.5)
                            `,
                            position: 'relative',
                            overflow: 'hidden',
                            transformStyle: 'preserve-3d',
                            height: effectiveView === 'compact' ? '100%' : 'auto',
                            minHeight: sizeConfig.minHeight || 'auto',
                            display: 'flex',
                            flexDirection: 'column' as const,
                            alignItems: effectiveView === 'compact' ? 'center' : 'flex-start',
                            justifyContent: effectiveView === 'compact' ? 'center' : 'flex-start',
                            textAlign: effectiveView === 'compact' ? 'center' : 'left'
                        };
                        const wrapperProps = tile.isChequered
                            ? { onClick: handleChequeredClick }
                            : { to: (FLAVOR === 'PRODUCTION' && tile.title === "Storefront") ? "" : tile.path };
                        
                        return (
                        <motion.div
                            key={index}
                            initial={{ opacity: 0, y: 30, scale: 0.9, rotateX: -15 }}
                            animate={{ opacity: 1, y: 0, scale: 1, rotateX: 0 }}
                            transition={{ 
                                duration: 0.5,
                                delay: index * 0.08,
                                ease: [0.34, 1.56, 0.64, 1]
                            }}
                            whileHover={{ 
                                y: -12, 
                                scale: 1.05,
                                rotateY: 5,
                                transition: { 
                                    duration: 0.3,
                                    type: "spring",
                                    stiffness: 300,
                                    damping: 20
                                }
                            }}
                            whileTap={{ scale: 0.95 }}
                            style={{ 
                                perspective: '1000px',
                                gridColumn: sizeConfig.gridColumn,
                                gridRow: sizeConfig.gridRow,
                                minHeight: sizeConfig.minHeight || 'auto'
                            }}
                        >
                            <Wrapper
                                {...wrapperProps}
                                className={`${wrapperClassName} ${tile.isChequered ? 'cursor-pointer' : ''}`}
                                style={wrapperStyle}
                            >
                            {/* Animated gradient background */}
                            <motion.div 
                                className={`absolute inset-0 bg-gradient-to-br ${tileGradient} opacity-0 group-hover:opacity-100 transition-opacity duration-700`}
                                animate={{
                                    backgroundPosition: ['0% 0%', '100% 100%'],
                                }}
                                transition={{
                                    duration: 8,
                                    repeat: Infinity,
                                    repeatType: "reverse",
                                    ease: "linear"
                                }}
                                style={{
                                    backgroundSize: '200% 200%'
                                }}
                            ></motion.div>
                            
                            {/* Floating particles effect */}
                            <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none">
                                {[...Array(6)].map((_, i) => (
                                    <motion.div
                                        key={i}
                                        className="absolute w-2 h-2 rounded-full bg-gradient-to-r from-blue-400 to-purple-400"
                                        style={{
                                            left: `${Math.random() * 100}%`,
                                            top: `${Math.random() * 100}%`,
                                        }}
                                        animate={{
                                            y: [0, -30, 0],
                                            x: [0, Math.random() * 20 - 10, 0],
                                            opacity: [0, 1, 0],
                                            scale: [0, 1, 0]
                                        }}
                                        transition={{
                                            duration: 3,
                                            repeat: Infinity,
                                            delay: i * 0.5,
                                            ease: "easeInOut"
                                        }}
                                    />
                                ))}
                            </div>
                            
                            {/* Shimmer effect */}
                            <motion.div
                                className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000"
                                style={{
                                    background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent)',
                                    width: '50%',
                                    height: '100%'
                                }}
                            ></motion.div>
                            
                            {/* Animated border glow */}
                            <motion.div
                                className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100"
                                style={{
                                    background: `linear-gradient(135deg, rgba(59, 130, 246, 0.3), rgba(139, 92, 246, 0.3), rgba(236, 72, 153, 0.3))`,
                                    padding: '2px',
                                    WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                                    WebkitMaskComposite: 'xor',
                                    maskComposite: 'exclude'
                                }}
                                animate={{
                                    rotate: [0, 360],
                                }}
                                transition={{
                                    duration: 20,
                                    repeat: Infinity,
                                    ease: "linear"
                                }}
                            ></motion.div>

                            {FLAVOR === 'PRODUCTION' && tile.title === "Storefront" && <ComingSoonRibbon />}
                            
                            {/* Special badge for My World */}
                            {tile.title === "My World" && (
                                <motion.div 
                                    className="absolute top-3 right-3 z-20"
                                    initial={{ scale: 0, rotate: -180, opacity: 0 }}
                                    animate={{ scale: 1, rotate: 0, opacity: 1 }}
                                    transition={{ delay: index * 0.08 + 0.3, type: "spring", stiffness: 300, damping: 15 }}
                                    whileHover={{ scale: 1.1, rotate: 5 }}
                                >
                                    <span className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-bold bg-gradient-to-r from-purple-500 via-pink-500 to-purple-500 text-white shadow-xl"
                                        style={{
                                            backgroundSize: '200% 200%',
                                            animation: 'gradientShift 3s ease infinite',
                                            boxShadow: '0 4px 15px rgba(139, 92, 246, 0.5)'
                                        }}>
                                        ✨ Personal
                                    </span>
                                </motion.div>
                            )}

                            <div className={`relative z-10 flex flex-col ${effectiveView === 'compact' ? 'items-center justify-center h-full' : 'h-full'}`}>
                                <motion.div 
                                    className={`flex items-center ${effectiveView === 'compact' ? 'mb-2' : isLarge ? 'mb-6' : effectiveView === 'grid' ? 'mb-4' : 'mb-2'} ${effectiveView === 'compact' ? 'justify-center' : ''}`}
                                    whileHover={{ scale: effectiveView === 'compact' ? 1.1 : 1.2, rotate: effectiveView === 'compact' ? 8 : 12, y: -5 }}
                                    transition={{ type: "spring", stiffness: 400, damping: 15 }}
                                >
                                    <motion.div 
                                        className={`tile-icon ${isLarge ? 'p-5' : effectiveView === 'grid' ? 'p-4' : isMobile ? 'p-2.5' : 'p-3'} rounded-xl ${
                                            tile.title === "My World" 
                                                ? "text-purple-600 bg-gradient-to-br from-purple-100 via-pink-50 to-purple-100" 
                                                : "text-blue-600 bg-gradient-to-br from-blue-50 via-indigo-50 to-blue-50"
                                        } group-hover:shadow-2xl transition-all duration-500 relative overflow-hidden`}
                                        style={{
                                            boxShadow: '0 8px 24px rgba(59, 130, 246, 0.3)',
                                            background: tile.title === "My World"
                                                ? 'linear-gradient(135deg, rgba(139, 92, 246, 0.15), rgba(236, 72, 153, 0.15), rgba(139, 92, 246, 0.15))'
                                                : 'linear-gradient(135deg, rgba(59, 130, 246, 0.15), rgba(99, 102, 241, 0.15), rgba(59, 130, 246, 0.15))',
                                            backgroundSize: '200% 200%',
                                            animation: 'gradientShift 4s ease infinite'
                                        }}
                                        whileHover={{
                                            boxShadow: '0 12px 32px rgba(59, 130, 246, 0.5)'
                                        }}
                                    >
                                        {/* Animated shine effect on icon */}
                                        <motion.div 
                                            className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent"
                                            animate={{
                                                x: ['-200%', '200%']
                                            }}
                                            transition={{
                                                duration: 2,
                                                repeat: Infinity,
                                                repeatDelay: 1,
                                                ease: "easeInOut"
                                            }}
                                        ></motion.div>
                                        
                                        {/* Icon glow effect */}
                                        <motion.div
                                            className="absolute inset-0 rounded-xl opacity-0 group-hover:opacity-100"
                                            style={{
                                                background: 'radial-gradient(circle, rgba(59, 130, 246, 0.3) 0%, transparent 70%)',
                                                filter: 'blur(10px)'
                                            }}
                                            animate={{
                                                scale: [1, 1.2, 1],
                                            }}
                                            transition={{
                                                duration: 2,
                                                repeat: Infinity,
                                                ease: "easeInOut"
                                            }}
                                        ></motion.div>
                                        
                                        <div className="relative z-10">
                                            {effectiveView === 'grid' || isLarge ? tile.icon : (
                                                <div className={isMobile ? "w-5 h-5" : "w-6 h-6"}>
                                                {tile.icon}
                                            </div>
                                        )}
                                    </div>
                                    </motion.div>
                                </motion.div>
                                {effectiveView === 'compact' ? (
                                    <motion.h3 
                                        className={`${isMobile ? 'text-xs' : 'text-sm'} font-bold ${tile.title === "My World" ? "text-purple-900" : "text-gray-900"} text-center line-clamp-2`}
                                        style={{
                                            background: tile.title === "My World" 
                                                ? 'linear-gradient(135deg, #9333ea 0%, #ec4899 50%, #9333ea 100%)'
                                                : 'linear-gradient(135deg, #1e40af 0%, #3b82f6 50%, #6366f1 100%)',
                                            backgroundSize: '200% 200%',
                                            WebkitBackgroundClip: 'text',
                                            WebkitTextFillColor: 'transparent',
                                            backgroundClip: 'text',
                                            animation: 'gradientShift 3s ease infinite'
                                        }}
                                        whileHover={{
                                            scale: 1.05,
                                            transition: { duration: 0.2 }
                                        }}
                                    >
                                        {tile.title}
                                    </motion.h3>
                                ) : (
                                    <div className="flex-grow flex flex-col">
                                        <motion.h3 
                                            className={`${isLarge ? 'text-2xl' : effectiveView === 'grid' ? 'text-xl' : 'text-sm'} font-extrabold ${isLarge ? 'mb-3' : effectiveView === 'grid' ? 'mb-2' : 'mb-1'} ${
                                        tile.title === "My World" ? "text-purple-900" : "text-gray-900"
                                            }`}
                                            style={{
                                                background: tile.title === "My World" 
                                                    ? 'linear-gradient(135deg, #9333ea 0%, #ec4899 50%, #9333ea 100%)'
                                                    : 'linear-gradient(135deg, #1e40af 0%, #3b82f6 50%, #6366f1 100%)',
                                                backgroundSize: '200% 200%',
                                                WebkitBackgroundClip: 'text',
                                                WebkitTextFillColor: 'transparent',
                                                backgroundClip: 'text',
                                                animation: 'gradientShift 3s ease infinite',
                                                filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1))'
                                            }}
                                            whileHover={{
                                                scale: 1.05,
                                                transition: { duration: 0.2 }
                                            }}
                                        >
                                            {tile.title}
                                        </motion.h3>
                                        {(effectiveView === 'grid' || isLarge || isTall) && (
                                            <motion.p 
                                                className={`${isLarge ? 'text-base' : 'text-sm'} leading-relaxed font-medium ${
                                            tile.title === "My World" ? "text-purple-700" : "text-gray-600"
                                                }`}
                                                initial={{ opacity: 0, y: 10 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                transition={{ delay: index * 0.08 + 0.2, duration: 0.4 }}
                                            >
                                            {tile.description}
                                            </motion.p>
                                    )}
                                </div>
                                )}
                                {/* Show images in grid mode for all tiles */}
                                {effectiveView === 'grid' && tile.image && (
                                    <motion.div 
                                        className={`absolute right-0 bottom-0 opacity-20 group-hover:opacity-100 transition-all duration-700 ease-in-out ${
                                            isLarge ? 'w-48 h-48' : isWide ? 'w-56 h-32' : isTall ? 'w-40 h-40' : 'w-36 h-36'
                                        }`}
                                        initial={{ scale: 0.8, rotate: -15, x: 20, y: 20 }}
                                        animate={{ 
                                            scale: [0.8, 0.85, 0.8],
                                            rotate: [-15, -10, -15]
                                        }}
                                        transition={{ 
                                            duration: 4,
                                            repeat: Infinity,
                                            ease: "easeInOut"
                                        }}
                                        whileHover={{ 
                                            scale: 1.2, 
                                            rotate: 10,
                                            x: -10,
                                            y: -10
                                        }}
                                    >
                                        <div className="relative w-full h-full">
                                            <motion.div
                                                className={`absolute inset-0 ${isLarge ? 'rounded-tl-3xl' : 'rounded-tl-2xl'} overflow-hidden`}
                                                style={{
                                                    background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.1), rgba(139, 92, 246, 0.1))',
                                                    backdropFilter: 'blur(10px)'
                                                }}
                                            >
                                        <img
                                            src={tile.image}
                                            alt=""
                                                    className="w-full h-full object-cover mix-blend-multiply"
                                            loading="lazy"
                                                    style={{
                                                        filter: 'drop-shadow(0 8px 16px rgba(0, 0, 0, 0.2)) brightness(1.1)'
                                                    }}
                                        />
                                            </motion.div>
                                    </div>
                                    </motion.div>
                                )}
                                
                                {/* Description overlay for Compact Mode on hover */}
                                {currentView === 'compact' && (
                                    <motion.div 
                                        className="absolute inset-0 bg-gradient-to-br from-blue-600/95 via-purple-600/95 to-pink-600/95 backdrop-blur-xl rounded-2xl opacity-0 group-hover:opacity-100 transition-all duration-500 flex flex-col items-center justify-center p-4 z-10"
                                        initial={{ scale: 0.8, rotateY: -90, opacity: 0 }}
                                        whileHover={{ scale: 1, rotateY: 0, opacity: 1 }}
                                        transition={{ type: "spring", stiffness: 300, damping: 25 }}
                                        style={{
                                            boxShadow: '0 25px 50px rgba(0, 0, 0, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.2)',
                                            border: '1px solid rgba(255, 255, 255, 0.2)'
                                        }}
                                    >
                                        {/* Animated background pattern */}
                                        <div className="absolute inset-0 opacity-20"
                                            style={{
                                                backgroundImage: 'radial-gradient(circle at 2px 2px, rgba(255, 255, 255, 0.3) 1px, transparent 0)',
                                                backgroundSize: '20px 20px'
                                            }}
                                        ></div>
                                        
                                        <motion.div 
                                            className="text-white text-xs text-center leading-tight relative z-10"
                                            initial={{ y: 20, opacity: 0 }}
                                            whileHover={{ y: 0, opacity: 1 }}
                                            transition={{ delay: 0.1, duration: 0.3 }}
                                        >
                                            <motion.div 
                                                className="flex items-center justify-center mb-4"
                                                whileHover={{ rotate: 360, scale: 1.3 }}
                                                transition={{ duration: 0.8, type: "spring", stiffness: 200 }}
                                            >
                                                <motion.div 
                                                    className="w-12 h-12 bg-white/40 rounded-2xl flex items-center justify-center backdrop-blur-md"
                                                    style={{
                                                        boxShadow: '0 8px 24px rgba(255, 255, 255, 0.3)',
                                                        border: '1px solid rgba(255, 255, 255, 0.3)'
                                                    }}
                                                    animate={{
                                                        boxShadow: [
                                                            '0 8px 24px rgba(255, 255, 255, 0.3)',
                                                            '0 8px 32px rgba(59, 130, 246, 0.5)',
                                                            '0 8px 24px rgba(255, 255, 255, 0.3)'
                                                        ]
                                                    }}
                                                    transition={{
                                                        duration: 2,
                                                        repeat: Infinity,
                                                        ease: "easeInOut"
                                                    }}
                                                >
                                                    {tile.icon}
                                                </motion.div>
                                            </motion.div>
                                            <motion.div 
                                                className="text-sm leading-relaxed font-bold mb-2"
                                                initial={{ opacity: 0, scale: 0.8 }}
                                                whileHover={{ opacity: 1, scale: 1 }}
                                                transition={{ delay: 0.2 }}
                                                style={{
                                                    textShadow: '0 2px 8px rgba(0, 0, 0, 0.3)'
                                                }}
                                            >
                                                {tile.title}
                                            </motion.div>
                                            <div className="text-xs leading-relaxed font-medium opacity-95"
                                                style={{
                                                    textShadow: '0 1px 4px rgba(0, 0, 0, 0.2)'
                                                }}
                                            >
                                                {tile.description}
                                            </div>
                                        </motion.div>
                                    </motion.div>
                                )}

                                {/* Premium arrow indicator on hover */}
                                <motion.div
                                    className="absolute bottom-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity duration-500 z-10"
                                    initial={{ x: -20, scale: 0 }}
                                    whileHover={{ x: 0, scale: 1 }}
                                    transition={{ type: "spring", stiffness: 300 }}
                                >
                                    <motion.div
                                        className="w-8 h-8 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 flex items-center justify-center shadow-lg"
                                        animate={{
                                            boxShadow: [
                                                '0 4px 12px rgba(59, 130, 246, 0.4)',
                                                '0 6px 20px rgba(139, 92, 246, 0.6)',
                                                '0 4px 12px rgba(59, 130, 246, 0.4)'
                                            ]
                                        }}
                                        transition={{
                                            duration: 2,
                                            repeat: Infinity,
                                            ease: "easeInOut"
                                        }}
                                    >
                                        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
                                        </svg>
                                    </motion.div>
                                </motion.div>
                            </div>
                            </Wrapper>
                        </motion.div>
                        );
                    })}
                </motion.section>

                {/* Colorful Book Demo Banner */}
                <div className="bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 p-5 sm:p-6 lg:p-8 rounded-xl shadow-lg overflow-hidden relative mt-6 sm:mt-8">
                    <div className="absolute inset-0 bg-white/10 backdrop-blur-sm"></div>
                    <div className="absolute inset-0 bg-gradient-to-r from-blue-600/20 via-purple-600/20 to-pink-600/20"></div>
                    
                    <div className="flex flex-col sm:flex-row items-center justify-between relative z-10 gap-4 sm:gap-6">
                        <div className="flex-1 text-center sm:text-left">
                            <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-white mb-2 sm:mb-3 leading-tight">Need Help Getting Started?</h3>
                            <p className="text-white/90 text-sm sm:text-base lg:text-lg leading-relaxed">Schedule a personalized demo with our team to explore all features and maximize your business potential</p>
                        </div>
                        <div className="w-full sm:w-auto sm:ml-6">
                            <Button
                                variant="contained"
                                size="medium"
                                startIcon={<CalendarMonthIcon />}
                                onClick={openCalendly}
                                className="w-full sm:w-auto bg-white text-blue-600 hover:bg-white/90 font-semibold px-6 sm:px-8 py-2.5 sm:py-3 text-sm sm:text-base rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 whitespace-nowrap"
                                style={pulseStyle}
                            >
                                Book Demo
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
            <DevFestivalTester />
        </main>
    );
} 